import { useResizeObserver } from '@orn/shared-web/hooks/useResizeObserver';
import * as React from 'react';

import { resolveAlertsOverlayFrame } from '../../utils/alert/alertsCenterCard';

import type {
  AlertsCenterPresentation,
  IAlertsCenterMotion,
  IAlertsOverlayFrame,
} from '../../utils/alert/alertsCenterCard';

export interface IAlertsOverlayFrameOptions {
  /** The pane root: the overlay's containing block, whose height is the full center's endpoint. */
  rootRef: React.RefObject<HTMLElement | null>;
  presentation: AlertsCenterPresentation;
  motion: IAlertsCenterMotion | undefined;
}

/**
 * The geometry the Notification Center overlay wears this frame. Keeps the
 * pane's height measured — on mount and on every resize — so the full
 * center's pixel endpoint is known before any move starts; the pure
 * `resolveAlertsOverlayFrame` does the rest.
 *
 * Measured continuously rather than at the first frame of a move: a move that
 * retargets to `full` mid-flight would otherwise commit once without a pin,
 * and the layout that measurement forces would make the browser transition
 * from the wrong height.
 *
 * @param options.rootRef The pane root — the overlay's containing block, whose height is the full center's endpoint.
 * @param options.presentation The presentation the card is in, or moving to.
 * @param options.motion The change in flight, or undefined at rest.
 * @returns The overlay's anchor and, while moving, the pixel height it wears this frame.
 */
export function useAlertsOverlayFrame({
  rootRef,
  presentation,
  motion,
}: IAlertsOverlayFrameOptions): IAlertsOverlayFrame {
  const [paneInnerHeightPx, setPaneInnerHeightPx] = React.useState<number | null>(null);

  const measure = React.useCallback(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    setPaneInnerHeightPx(root.clientHeight);
  }, [rootRef]);

  React.useLayoutEffect(measure, [measure]);
  useResizeObserver({ ref: rootRef, onResize: measure });

  return resolveAlertsOverlayFrame({ presentation, motion, paneInnerHeightPx });
}
